
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";

export interface AvailableTimeSlot {
  time: string;
  available: boolean;
  duration?: number;
  price?: number;
}

export const getAvailableTimeSlotsForDate = async (
  psychologistId: string,
  date: Date
): Promise<AvailableTimeSlot[]> => {
  try {
    const dateString = format(date, 'yyyy-MM-dd');
    const dayOfWeek = date.getDay();

    // Buscar disponibilidade recorrente para este dia da semana
    const { data: availability, error: availabilityError } = await supabase
      .from('psychologist_availability')
      .select('start_time, end_time, duration')
      .eq('psychologist_id', psychologistId)
      .eq('day_of_week', dayOfWeek)
      .eq('is_recurring', true);

    if (availabilityError) {
      throw availabilityError;
    }

    if (!availability || availability.length === 0) {
      return [];
    }

    // Buscar consultas já agendadas para esta data
    const { data: appointments, error: appointmentsError } = await supabase
      .from('appointments')
      .select('time, duration')
      .eq('psychologist_id', psychologistId)
      .eq('date', dateString)
      .in('status', ['confirmed', 'pending']);

    if (appointmentsError) {
      throw appointmentsError;
    }

    const occupiedSlots = appointments?.map(apt => apt.time) || [];

    // Gerar slots de 30 em 30 minutos baseados na disponibilidade
    const availableSlots: AvailableTimeSlot[] = [];

    availability.forEach(slot => {
      const startTime = slot.start_time;
      const endTime = slot.end_time;
      
      // Converter para minutos
      const [startHour, startMin] = startTime.split(':').map(Number);
      const [endHour, endMin] = endTime.split(':').map(Number);
      
      let currentMinutes = startHour * 60 + startMin;
      const endMinutes = endHour * 60 + endMin;

      while (currentMinutes < endMinutes) {
        const hour = Math.floor(currentMinutes / 60);
        const minute = currentMinutes % 60;
        const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        
        const isOccupied = occupiedSlots.includes(timeString);
        
        availableSlots.push({
          time: timeString,
          available: !isOccupied,
          duration: slot.duration || 60
        });

        currentMinutes += 30; // Incrementar 30 minutos
      }
    });

    return availableSlots.sort((a, b) => a.time.localeCompare(b.time));
  } catch (error) {
    console.error('Error fetching available time slots:', error);
    return [];
  }
};

export const checkTimeSlotConflict = async (
  psychologistId: string,
  date: string,
  time: string
): Promise<boolean> => {
  try {
    const { data: conflictingAppointments, error } = await supabase
      .from('appointments')
      .select('id')
      .eq('psychologist_id', psychologistId)
      .eq('date', date)
      .eq('time', time)
      .in('status', ['confirmed', 'pending']); // Include both confirmed and pending

    if (error) {
      throw error;
    }

    return (conflictingAppointments && conflictingAppointments.length > 0);
  } catch (error) {
    console.error('Error checking time slot conflict:', error);
    return true; // Assumir conflito em caso de erro por segurança
  }
};

// Função para verificar se um psicólogo tem disponibilidade configurada
export const checkPsychologistHasAvailability = async (
  psychologistId: string
): Promise<boolean> => {
  try {
    const { data: availability, error } = await supabase
      .from('psychologist_availability')
      .select('id')
      .eq('psychologist_id', psychologistId)
      .limit(1);

    if (error) {
      throw error;
    }

    return availability && availability.length > 0;
  } catch (error) {
    console.error('Error checking psychologist availability:', error);
    return false;
  }
};

// Função para buscar todos os dias com disponibilidade do psicólogo
export const getPsychologistAvailableDays = async (
  psychologistId: string
): Promise<number[]> => {
  try {
    const { data: availability, error } = await supabase
      .from('psychologist_availability')
      .select('day_of_week')
      .eq('psychologist_id', psychologistId)
      .eq('is_recurring', true);

    if (error) {
      throw error;
    }

    const uniqueDays = [...new Set(availability?.map(slot => slot.day_of_week) || [])];
    return uniqueDays.sort();
  } catch (error) {
    console.error('Error fetching psychologist available days:', error);
    return [];
  }
};
